import { readdirSync, statSync, readFileSync } from "node:fs";
import { join } from "node:path";
import type { AgentConnection } from "../connection/agent-connection.js";
import { configHomePlansDir } from "../harness/pty/settings.js";

export class PlanSync {
  private planFileSnapshot = new Map<string, number>();
  private lockedPlanFile: string | null = null;
  private workspaceDir: string;
  private connection: AgentConnection;

  constructor(workspaceDir: string, connection: AgentConnection) {
    this.workspaceDir = workspaceDir;
    this.connection = connection;
  }

  updateWorkspaceDir(workspaceDir: string): void {
    this.workspaceDir = workspaceDir;
  }

  private getPlanDirs(): string[] {
    // Claude Code 2.x writes plan-mode plan files to <config-home>/plans, not
    // the workspace .claude/plans — watch both. The snapshot taken at query
    // start keeps stale files from other sessions out of detection.
    return [join(this.workspaceDir, ".claude", "plans"), configHomePlansDir()];
  }

  snapshotPlanFiles(): void {
    this.planFileSnapshot.clear();
    this.lockedPlanFile = null;
    for (const plansDir of this.getPlanDirs()) {
      try {
        for (const file of readdirSync(plansDir).filter((f) => f.endsWith(".md"))) {
          try {
            const fullPath = join(plansDir, file);
            const stat = statSync(fullPath);
            this.planFileSnapshot.set(fullPath, stat.mtimeMs);
          } catch {
            continue;
          }
        }
      } catch {
        /* plans dir doesn't exist yet */
      }
    }
  }

  private findNewestPlanFile(): { path: string; mtime: number } | null {
    let newest: { path: string; mtime: number } | null = null;
    for (const plansDir of this.getPlanDirs()) {
      let files: string[];
      try {
        files = readdirSync(plansDir).filter((f) => f.endsWith(".md"));
      } catch {
        continue;
      }

      for (const file of files) {
        const fullPath = join(plansDir, file);
        try {
          const stat = statSync(fullPath);
          const prevMtime = this.planFileSnapshot.get(fullPath);
          const isNew = prevMtime === undefined || stat.mtimeMs > prevMtime;
          if (isNew && (!newest || stat.mtimeMs > newest.mtime)) {
            newest = { path: fullPath, mtime: stat.mtimeMs };
          }
        } catch {
          continue;
        }
      }
    }
    return newest;
  }

  async syncPlanFile(): Promise<void> {
    const isInitialDetection = this.lockedPlanFile === null;
    const target = this.lockedPlanFile ?? this.findNewestPlanFile()?.path ?? null;
    if (!target) return;

    // Lock on initial detection before reading — matches prior behavior and
    // prevents a broken file from re-triggering detection on every tick.
    if (isInitialDetection) {
      this.lockedPlanFile = target;
    }

    const fileName = target.split("/").pop() ?? "plan";

    let content: string;
    try {
      content = readFileSync(target, "utf-8").trim();
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      this.connection.postChatMessage(
        `Plan sync warning: could not read local plan file (${fileName}): ${reason}. The task plan was not updated.`,
      );
      return;
    }

    if (!content) return;

    const result = await this.connection.updateTaskFields({ plan: content });
    if (!result.ok) {
      this.connection.postChatMessage(
        `Plan sync failed: the local plan file (${fileName}) was not persisted to the task (${result.error ?? "unknown error"}). Retry update_task_plan or re-run ExitPlanMode.`,
      );
      return;
    }

    const verb = isInitialDetection ? "Detected local plan file" : "Synced local plan file";
    const suffix = isInitialDetection ? " and synced it to the task plan." : " to the task plan.";
    this.connection.postChatMessage(`${verb} (${fileName})${suffix}`);
  }
}
